import { MonthlyActualValue, MonthlyTargetValue } from './types'

export interface MonthRow {
  month: number
  monthlyTarget?: MonthlyTargetValue
  lastYearOutcome?: number
  isCurrentMonth: boolean
  /** Past and current months can't be edited. */
  isLocked: boolean
}

/**
 * A month is locked for editing once it has started: every month of a past year, and the
 * months up to and including the current one in the current year. Future months stay editable.
 */
export function isMonthLocked(year: number, month: number, now: Date = new Date()): boolean {
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1
  return year < currentYear || (year === currentYear && month <= currentMonth)
}

export interface DerivedMonthlyRows {
  rows: MonthRow[]
  hasLastYear: boolean
  targetSum: number
  lastYearSum: number
  /** Months that can be compared: they have a last-year actual and a target above 0. */
  comparableMonthCount: number
  /** Sum of the targets of the comparable months (like-for-like). */
  comparableTargetSum: number
  /** Sum of the last-year actuals of the comparable months. */
  comparableLastYearSum: number
}

/**
 * Single source of truth for deriving the per-month view model shared by the desktop table
 * (MonthlyTargetsTable) and the mobile list (MonthlyTargetsList): current-month detection,
 * last-year lookup and sums.
 */
export function deriveMonthlyRows(
  months: number[],
  monthlyTargets: MonthlyTargetValue[],
  lastYearActuals: MonthlyActualValue[] | undefined,
  targetYear: number,
): DerivedMonthlyRows {
  const currentMonth = new Date().getMonth() + 1
  const currentYear = new Date().getFullYear()
  const hasLastYear = !!lastYearActuals

  const rows: MonthRow[] = months.map((month) => {
    const monthlyTarget = monthlyTargets.find((mt) => mt.month === month)
    const lastYearOutcome = lastYearActuals?.find((data) => data.month === month)?.value
    const isCurrentMonth = month === currentMonth && monthlyTarget?.year === currentYear
    return { month, monthlyTarget, lastYearOutcome, isCurrentMonth, isLocked: isMonthLocked(targetYear, month) }
  })

  const targetSum = rows.reduce((sum, r) => sum + (r.monthlyTarget?.value ?? 0), 0)
  const lastYearSum = rows.reduce((sum, r) => sum + (r.lastYearOutcome ?? 0), 0)

  const comparableRows = rows.filter((r) => r.lastYearOutcome !== undefined && (r.monthlyTarget?.value ?? 0) > 0)
  const comparableTargetSum = comparableRows.reduce((sum, r) => sum + (r.monthlyTarget?.value ?? 0), 0)
  const comparableLastYearSum = comparableRows.reduce((sum, r) => sum + (r.lastYearOutcome ?? 0), 0)

  return {
    rows,
    hasLastYear,
    targetSum,
    lastYearSum,
    comparableMonthCount: comparableRows.length,
    comparableTargetSum,
    comparableLastYearSum,
  }
}
